// ═══════════════════════════════════════════════════════════════
// LmsCourseFormModal — création / édition des métadonnées d'un cours
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { GraduationCap, Save } from 'lucide-react';
import { lmsApi } from '../../services/lmsApi';
import { toast } from '../ui/Toast';
import { Modal, Field, TextInput, TextArea, Select, s, Spinner, slugify } from './lmsUi';

const LEVELS = [
    { v: 'beginner', l: 'Débutant' },
    { v: 'intermediate', l: 'Intermédiaire' },
    { v: 'advanced', l: 'Avancé' },
];

export default function LmsCourseFormModal({ course, onClose, onSaved }) {
    const editing = !!course;
    const [f, setF] = useState({
        title: course?.title || '',
        slug: course?.slug || '',
        subtitle: course?.subtitle || '',
        description: course?.description || '',
        level: course?.level || 'beginner',
        language: course?.language || 'fr',
        price_eur: course?.price_eur != null ? String(course.price_eur) : '',
        duration_hours: course?.duration_hours != null ? String(course.duration_hours) : '',
        cover_image_url: course?.cover_image_url || '',
        max_tutor_messages: course?.max_tutor_messages != null ? String(course.max_tutor_messages) : '',
    });
    const [slugTouched, setSlugTouched] = useState(editing);
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
    const onTitle = (v) => {
        set('title', v);
        if (!slugTouched) set('slug', slugify(v));
    };

    const canSave = f.title.trim() && (editing || f.slug.trim());

    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const payload = {
                title: f.title.trim(),
                subtitle: f.subtitle.trim() || null,
                description: f.description.trim() || null,
                level: f.level,
                language: f.language.trim() || 'fr',
                price_eur: f.price_eur === '' ? null : parseFloat(f.price_eur),
                duration_hours: f.duration_hours === '' ? null : parseInt(f.duration_hours, 10),
                cover_image_url: f.cover_image_url.trim() || null,
                max_tutor_messages: f.max_tutor_messages === '' ? null : parseInt(f.max_tutor_messages, 10),
            };
            let result;
            if (editing) {
                result = (await lmsApi.updateCourse(course.id, payload)).course;
                toast.success('Cours mis à jour');
            } else {
                result = (await lmsApi.createCourse({ ...payload, slug: f.slug.trim() })).course;
                toast.success('Cours créé');
            }
            onSaved?.(result);
        } catch (e) {
            toast.error(e.message || 'Échec de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={editing ? 'Modifier le cours' : 'Nouveau cours'}
            subtitle={editing ? course.title : 'Définis les fondations de ta formation'}
            icon={GraduationCap}
            onClose={onClose}
            busy={saving}
            width={600}
            footer={
                <>
                    <button style={s.ghostBtn} onClick={onClose} disabled={saving}>Annuler</button>
                    <button style={{ ...s.primaryBtn, opacity: canSave && !saving ? 1 : 0.5 }} onClick={save} disabled={!canSave || saving}>
                        {saving ? <Spinner /> : <Save size={14} />} {editing ? 'Enregistrer' : 'Créer le cours'}
                    </button>
                </>
            }
        >
            <Field label="Titre du cours">
                <TextInput value={f.title} onChange={(e) => onTitle(e.target.value)} placeholder="Ex. Souveraineté monétaire — les fondamentaux" autoFocus />
            </Field>

            <Field label="Slug (URL)" hint={editing ? 'Le slug ne peut pas être modifié après création.' : 'Identifiant dans l\'URL — généré depuis le titre, modifiable.'}>
                <TextInput
                    value={f.slug}
                    onChange={(e) => { setSlugTouched(true); set('slug', slugify(e.target.value)); }}
                    placeholder="souverainete-monetaire"
                    disabled={editing}
                    style={editing ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                />
            </Field>

            <Field label="Sous-titre">
                <TextInput value={f.subtitle} onChange={(e) => set('subtitle', e.target.value)} placeholder="Une accroche courte affichée au catalogue" />
            </Field>

            <Field label="Description">
                <TextArea value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="Ce que l'apprenant va comprendre, pour qui, et pourquoi. (markdown supporté)" />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Niveau">
                    <Select value={f.level} onChange={(e) => set('level', e.target.value)}>
                        {LEVELS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
                    </Select>
                </Field>
                <Field label="Langue">
                    <TextInput value={f.language} onChange={(e) => set('language', e.target.value)} placeholder="fr" />
                </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Prix (€)" hint="Vide = gratuit. Un cours payant force le passage par le paiement.">
                    <TextInput type="number" min="0" step="0.01" value={f.price_eur} onChange={(e) => set('price_eur', e.target.value)} placeholder="0" />
                </Field>
                <Field label="Durée estimée (h)">
                    <TextInput type="number" min="0" step="1" value={f.duration_hours} onChange={(e) => set('duration_hours', e.target.value)} placeholder="ex. 6" />
                </Field>
            </div>

            <Field label="Image de couverture (URL)">
                <TextInput value={f.cover_image_url} onChange={(e) => set('cover_image_url', e.target.value)} placeholder="https://…" />
            </Field>

            <Field label="Plafond messages tuteur IA / apprenant" hint="Vide = illimité. Limite le coût LLM du tutorat par cours.">
                <TextInput type="number" min="0" step="1" value={f.max_tutor_messages} onChange={(e) => set('max_tutor_messages', e.target.value)} placeholder="ex. 50" />
            </Field>
        </Modal>
    );
}
